import React, { useMemo, useState } from 'react';

import { Popover, Typography } from '@mui/material';
import { ChevronDown, ChevronsUp, ChevronUp, CircleAlert, Minus } from 'lucide-react';
import { PICKER_CHIP_ICON_SIZE, usePickerChipStyles } from 'src/components/generic';
import { FilterPopoverContent } from 'src/components/workflow/workflow-list/filter-contents/FilterPopoverContent';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

export const PRIORITY_COLORS: Record<Apollo.WorkflowPriority, string> = {
  [Apollo.WorkflowPriority.Urgent]: '#FF4059',
  [Apollo.WorkflowPriority.High]: '#EA580C',
  [Apollo.WorkflowPriority.Medium]: '#E87A14',
  [Apollo.WorkflowPriority.Low]: '#146EE8',
};

export function getPriorityColor(priority: Apollo.WorkflowPriority | null): string {
  if (!priority) return '#324A52';
  return PRIORITY_COLORS[priority];
}

function isWorkflowPriority(value: string): value is Apollo.WorkflowPriority {
  return (Object.values(Apollo.WorkflowPriority) as string[]).includes(value);
}

const PLACEHOLDER_GLYPH_SIZE = 12;

const useStyles = makeStyles()(theme => ({
  icon: {
    color: theme.palette.text.secondary,
  },
  iconContainer: {
    alignItems: 'center',
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 4,
    boxSizing: 'border-box',
    display: 'flex',
    flexShrink: 0,
    height: PICKER_CHIP_ICON_SIZE,
    justifyContent: 'center',
    width: PICKER_CHIP_ICON_SIZE,
  },
  optionContent: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
  placeholderLabel: {
    color: theme.palette.text.secondary,
  },
  popoverContent: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1),
  },
  priorityIconWrapper: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    width: PICKER_CHIP_ICON_SIZE,
  },
}));

export const PRIORITIES = [
  { id: Apollo.WorkflowPriority.Urgent, label: 'Urgent' },
  { id: Apollo.WorkflowPriority.High, label: 'High' },
  { id: Apollo.WorkflowPriority.Medium, label: 'Medium' },
  { id: Apollo.WorkflowPriority.Low, label: 'Low' },
];

interface PriorityIconSwitchProps {
  priority: Apollo.WorkflowPriority | null;
  size?: number;
}

export function PriorityIconSwitch({
  priority,
  size = 10,
}: PriorityIconSwitchProps): JSX.Element | null {
  const color = priority ? PRIORITY_COLORS[priority] : undefined;

  switch (priority) {
    case Apollo.WorkflowPriority.Urgent:
      return <CircleAlert color={color} size={size} />;
    case Apollo.WorkflowPriority.High:
      return <ChevronsUp color={color} size={size} />;
    case Apollo.WorkflowPriority.Medium:
      return <ChevronUp color={color} size={size} />;
    case Apollo.WorkflowPriority.Low:
      return <ChevronDown color={color} size={size} />;
    default:
      return null;
  }
}

export interface PriorityDisplayProps {
  priority: Apollo.WorkflowPriority | null;
}

export function PriorityDisplay({ priority }: PriorityDisplayProps): JSX.Element {
  const { classes, cx } = useStyles();
  const color = getPriorityColor(priority);
  const { pickerChipContentClassName, pickerChipIconClassName, pickerChipLabelClassName } =
    usePickerChipStyles();

  if (priority)
    return (
      <div className={pickerChipContentClassName} style={{ color }}>
        <div className={pickerChipIconClassName}>
          <PriorityIconSwitch priority={priority} size={PICKER_CHIP_ICON_SIZE} />
        </div>
        <Typography noWrap className={pickerChipLabelClassName} color="inherit" variant="body2">
          {priority}
        </Typography>
      </div>
    );

  return (
    <div className={pickerChipContentClassName}>
      <div className={classes.iconContainer}>
        <Minus className={classes.icon} size={PLACEHOLDER_GLYPH_SIZE} />
      </div>
      <Typography
        noWrap
        className={cx(pickerChipLabelClassName, classes.placeholderLabel)}
        variant="body2"
      >
        Add priority
      </Typography>
    </div>
  );
}

interface WorkflowPriorityProps {
  onPriorityChange: (priority: Apollo.WorkflowPriority) => void | Promise<void>;
  priority: Apollo.WorkflowPriority | null;
  renderTrigger: (priority: Apollo.WorkflowPriority | null) => React.ReactNode;
}

export function WorkflowPriority({
  onPriorityChange,
  priority,
  renderTrigger,
}: WorkflowPriorityProps): JSX.Element {
  const { classes } = useStyles();
  const { getPickerChipClassName } = usePickerChipStyles();
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const selectedIds = useMemo(() => new Set(priority ? [priority] : []), [priority]);

  return (
    <>
      <div
        ref={setAnchorEl}
        className={getPickerChipClassName(open)}
        onClick={e => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        {renderTrigger(priority)}
      </div>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          horizontal: 'left',
          vertical: 'bottom',
        }}
        open={open}
        slotProps={{
          paper: {
            onClick: (e: React.MouseEvent) => e.stopPropagation(),
          },
        }}
        transformOrigin={{
          horizontal: 'left',
          vertical: 'top',
        }}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        onClose={() => {
          setOpen(false);
        }}
      >
        <div className={classes.popoverContent}>
          <FilterPopoverContent
            options={PRIORITIES}
            renderOption={(option, isSelected) => {
              const color = PRIORITY_COLORS[option.id];
              return (
                <div
                  className={classes.optionContent}
                  style={{
                    color,
                    fontWeight: isSelected ? 600 : undefined,
                  }}
                >
                  <div className={classes.priorityIconWrapper}>
                    <PriorityIconSwitch priority={option.id} size={14} />
                  </div>
                  <Typography color="inherit" variant="body2">
                    {option.label}
                  </Typography>
                </div>
              );
            }}
            selectedIds={selectedIds}
            showCheckbox={false}
            onToggle={priorityId => {
              if (!isWorkflowPriority(priorityId)) return;
              onPriorityChange(priorityId);
              setOpen(false);
            }}
          />
        </div>
      </Popover>
    </>
  );
}
